import { memo, useCallback, useEffect, useState } from 'react';
import { Descendant, Node } from 'slate';
import {
  Editable,
  RenderElementProps,
  RenderLeafProps,
  Slate
} from 'slate-react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { LiveDraft } from '@utils/editors/live-transcript-state.ts';
import { LiveUtterance } from '@utils/types/editors/live-transcript.ts';
import { DocumentTranscriptTextV1 } from '@utils/types/editors/transcripts/transcript.ts';

import {
  LiveEditorCache,
  registerLiveEditor,
  restoreLiveEditor,
  retainLiveEditor
} from './live-editor-cache.ts';
export type { LiveEditorCache } from './live-editor-cache.ts';

type Props = {
  row: LiveDraft;
  canEdit: boolean;
  cache: LiveEditorCache;
  update: (id: string, value: LiveUtterance) => void;
  updateSpeaker: (id: string, value: string) => void;
};

export const LiveEditorRow = memo(
  ({ row, canEdit, cache, update, updateSpeaker }: Props) => {
    const { t } = useTranslation();
    const { getColour } = useThemeContext();
    const { segment } = row;
    const cacheKey = `${segment.segment_id}:${row.editorEpoch ?? 0}`;
    const [{ editor, initialValue }] = useState(() => {
      const children = segment.utterance.children.length
        ? segment.utterance.children
        : [{ type: 'paragraph' as const, children: [{ text: '' }] }];
      return restoreLiveEditor(cache, cacheKey, children as Descendant[]);
    });
    useEffect(() => {
      registerLiveEditor(cache, cacheKey);
      return () => retainLiveEditor(cache, cacheKey, editor);
    }, [cache, cacheKey, editor]);
    const onChange = useCallback(
      (value: Descendant[]) => {
        if (
          !canEdit ||
          !editor.operations.some(
            operation => operation.type !== 'set_selection'
          )
        )
          return;
        const children = value.map(node => ({
          ...node,
          type: 'paragraph' as const,
          children:
            'children' in node
              ? (node.children as DocumentTranscriptTextV1[])
              : [{ text: Node.string(node) }]
        }));
        update(segment.segment_id, {
          ...segment.utterance,
          children: structuredClone(children)
        });
      },
      [canEdit, editor, segment.segment_id, segment.utterance, update]
    );
    const renderElement = useCallback(
      ({ attributes, children }: RenderElementProps) => (
        <p {...attributes} className="m-0 leading-relaxed">
          {children}
        </p>
      ),
      []
    );
    const renderLeaf = useCallback(
      ({ attributes, children, leaf }: RenderLeafProps) => {
        const text = leaf as DocumentTranscriptTextV1;
        return (
          <span
            {...attributes}
            style={{
              fontWeight: text.bold ? 'bold' : undefined,
              fontStyle: text.italic ? 'italic' : undefined,
              textDecoration: text.underlined
                ? 'underline'
                : text.strike
                  ? 'line-through'
                  : undefined,
              color: text.color || undefined
            }}
          >
            {children}
          </span>
        );
      },
      []
    );

    return (
      <article
        className="rounded-xl border p-4"
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('thirdLayer')
        }}
        data-segment-id={segment.segment_id}
      >
        <div className="flex items-center gap-3 mb-2 text-sm">
          <span style={{ color: getColour('inactiveText') }}>
            {new Date(segment.utterance.start ?? 0).toISOString().slice(11, 19)}
          </span>
          <input
            aria-label={`${t('speaker')} ${segment.sequence}`}
            value={segment.utterance.speaker ?? ''}
            readOnly={!canEdit}
            placeholder={t('speaker')}
            className="min-w-0 flex-1 border-0 bg-transparent py-1 font-medium outline-none focus-visible:rounded-md focus-visible:ring-2"
            style={{
              color: getColour('normalText')
            }}
            onChange={event =>
              updateSpeaker(segment.segment_id, event.target.value)
            }
          />
          {row.dirty && (
            <span style={{ color: getColour('inactiveText') }}>
              {t('live-unsaved')}
            </span>
          )}
        </div>
        <Slate editor={editor} initialValue={initialValue} onChange={onChange}>
          <Editable
            aria-label={`${t('live-segment')} ${segment.sequence}`}
            readOnly={!canEdit}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            spellCheck
            className="outline-none text-lg"
            style={{ color: getColour('normalText') }}
          />
        </Slate>
      </article>
    );
  }
);
LiveEditorRow.displayName = 'LiveEditorRow';
